import {
  Component,
  input,
  output,
  signal,
  computed,
  effect
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';

import { ContactoAdicional } from 'src/app/domain/model/cumplimiento/cumplimiento';
import {
  isValidEmail,
  sanitizeEmail,
  emailKeypress,
  sanitizeNombreContacto
} from 'src/app/common/input-utils';
import { isValidEmailLegacy } from 'src/app/common/input-utils';

// Re-exportar para uso externo
export type { ContactoAdicional };

/**
 * DUMB COMPONENT: ContactosAdicionalesDialog
 *
 * Modal para agregar hasta 3 contactos adicionales al cumplimiento.
 *
 * Migrado de: AdicionarContactosViewImpl.java
 *
 * @example
 * <app-contactos-adicionales-dialog
 *   [visible]="mostrarContactosDialog()"
 *   [contactos]="contactosAdicionales()"
 *   (visibleChange)="mostrarContactosDialog.set($event)"
 *   (guardar)="onGuardarContactos($event)" />
 */
@Component({
  selector: 'app-contactos-adicionales-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    TableModule,
    TooltipModule
  ],
  template: `
    <p-dialog
      header="Adicionar contactos"
      [(visible)]="dialogVisible"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '38rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '768px': '94vw', '640px': '96vw' }"
      [closable]="false"
      (onHide)="onCancelar()"
    >
      <!-- Errores de validación -->
      @if (errores().length > 0) {
        <div class="mb-4 p-3 bg-red-50 border border-red-200 rounded">
          @for (error of errores(); track error) {
            <div class="text-red-700 text-sm">
              <i class="pi pi-exclamation-circle mr-1"></i>{{ error }}
            </div>
          }
        </div>
      }

      <!-- Contactos: cards en mobile, tabla en desktop -->
      <div class="md:hidden space-y-3">
        @for (
          contacto of contactosInterno();
          track $index;
          let i = $index
        ) {
          <article
            class="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800"
          >
            <div class="flex items-start justify-between gap-2 mb-2">
              <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-200">
                Contacto {{ i + 1 }}
              </h3>
              <button
                pButton
                type="button"
                icon="pi pi-trash"
                class="p-button-text p-button-danger p-button-sm min-w-[44px] min-h-[44px]"
                (click)="limpiarFila(i)"
                title="Quitar contacto"
                pTooltip="Quitar contacto"
              ></button>
            </div>

            <div class="flex flex-col gap-2">
              <div>
                <label class="text-xs text-gray-500 dark:text-gray-400 block mb-1"
                  >Nombre del contacto</label
                >
                <input
                  pInputText
                  [(ngModel)]="contacto.dsnombre"
                  (change)="contacto.dsnombre = sanitizarNombre(contacto.dsnombre)"
                  class="w-full"
                  maxlength="50"
                  placeholder="Nombre..."
                />
              </div>
              <div>
                <label class="text-xs text-gray-500 dark:text-gray-400 block mb-1"
                  >Email del contacto</label
                >
                <input
                  pInputText
                  [(ngModel)]="contacto.dsemail"
                  (keypress)="emailKeypress($event)"
                  (input)="contacto.dsemail = sanitizeEmail(contacto.dsemail)"
                  class="w-full"
                  type="email"
                  maxlength="100"
                  placeholder="email@ejemplo.com"
                />
              </div>
            </div>
          </article>
        }
      </div>

      <div class="hidden md:block overflow-x-auto">
        <table class="w-full border-collapse">
          <thead>
            <tr class="bg-gray-100">
              <th
                class="p-2 text-left text-sm font-medium text-gray-700 border"
              >
                Nombre del contacto
              </th>
              <th
                class="p-2 text-left text-sm font-medium text-gray-700 border"
              >
                Email del contacto
              </th>
              <th
                class="p-2 text-center text-sm font-medium text-gray-700 border w-16"
              >
                Quitar
              </th>
            </tr>
          </thead>
          <tbody>
            @for (
              contacto of contactosInterno();
              track $index;
              let i = $index
            ) {
              <tr>
                <td class="p-2 border">
                  <input
                    pInputText
                    [(ngModel)]="contacto.dsnombre"
                    (change)="contacto.dsnombre = sanitizarNombre(contacto.dsnombre)"
                    class="w-full"
                    maxlength="50"
                    placeholder="Nombre..."
                  />
                </td>
                <td class="p-2 border">
                  <input
                    pInputText
                    [(ngModel)]="contacto.dsemail"
                    (keypress)="emailKeypress($event)"
                    (input)="contacto.dsemail = sanitizeEmail(contacto.dsemail)"
                    class="w-full"
                    type="email"
                    maxlength="100"
                    placeholder="email@ejemplo.com"
                  />
                </td>
                <td class="p-2 border text-center">
                  <button
                    pButton
                    type="button"
                    icon="pi pi-trash"
                    class="p-button-text p-button-danger p-button-sm min-w-[44px] min-h-[44px]"
                    (click)="limpiarFila(i)"
                    title="Quitar contacto"
                pTooltip="Quitar contacto"
                  ></button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <!-- Legacy (AdicionarContactosView): único botón «Ocultar», que valida y guarda.
               Se rotula «Aceptar» por claridad para el usuario; el comportamiento es el mismo. -->
          <button
            pButton
            type="button"
            label="Aceptar"
            icon="pi pi-check"
            class="min-h-[44px]"
            (click)="onGuardar()"
          ></button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class ContactosAdicionalesDialogComponent {
  // === Inputs ===
  visible = input<boolean>(false);
  contactos = input<ContactoAdicional[]>([]);

  // === Outputs ===
  visibleChange = output<boolean>();
  guardar = output<ContactoAdicional[]>();

  // === State ===
  dialogVisible = false;
  readonly errores = signal<string[]>([]);

  // Utilidades de sanitización expuestas al template
  sanitizeEmail = sanitizeEmail;
  emailKeypress = emailKeypress;

  /** Nombre del contacto adicional: solo letras y espacios (legacy cleanString; backend PERSON_NAME). */
  sanitizarNombre(valor: string): string {
    return sanitizeNombreContacto(valor);
  }

  // Estado interno editable (3 filas siempre)
  readonly contactosInterno = signal<ContactoAdicional[]>([
    { dsnombre: '', dsemail: '' },
    { dsnombre: '', dsemail: '' },
    { dsnombre: '', dsemail: '' }
  ]);

  constructor() {
    // Efecto para sincronizar visible input con dialogVisible
    // y cargar contactos cuando se abre
    effect(() => {
      const isVisible = this.visible();
      if (isVisible) {
        this.dialogVisible = true;
        this.cargarContactos();
      }
    });
  }

  /**
   * Carga los contactos existentes en el estado interno
   */
  private cargarContactos(): void {
    const existentes = this.contactos() || [];
    // Copia defensiva: el diálogo edita su propio estado y solo "Adicionar" lo devuelve al padre.
    // Legacy (AdicionarContactosViewImpl): cada apertura trabaja sobre una copia de los datos.
    const copiar = (c?: ContactoAdicional): ContactoAdicional =>
      c ? { ...c } : { dsnombre: '', dsemail: '' };
    const nuevos: ContactoAdicional[] = [
      copiar(existentes[0]),
      copiar(existentes[1]),
      copiar(existentes[2])
    ];
    this.contactosInterno.set(nuevos);
    this.errores.set([]);
  }

  /**
   * Limpia una fila específica
   * Legacy: onClickDelete()
   */
  limpiarFila(index: number): void {
    this.contactosInterno.update((lista) => {
      const nueva = [...lista];
      nueva[index] = { dsnombre: '', dsemail: '' };
      return nueva;
    });
  }

  /**
   * Réplica de AdicionarContactosViewImpl.validarFormulario del legacy:
   * 1. Primero el formato de los emails diligenciados (Utilidades.isValidEmail / REGEX_EMAIL).
   * 2. Luego la completitud: si hay nombre debe haber email y viceversa.
   * Se informa solo el primer error, con el texto del legacy.
   */
  private validar(): boolean {
    const contactos = this.contactosInterno();
    const lleno = (v: string | undefined | null) => !!v && v.trim().length > 0;

    for (let i = 0; i < contactos.length; i++) {
      const email = contactos[i].dsemail;
      if (lleno(email) && !isValidEmailLegacy(email)) {
        this.errores.set([`El formato del E-mail adicional numero ${i + 1} es invalido`]);
        return false;
      }
    }

    for (let i = 0; i < contactos.length; i++) {
      const tieneNombre = lleno(contactos[i].dsnombre);
      const tieneEmail = lleno(contactos[i].dsemail);
      if (tieneNombre !== tieneEmail) {
        this.errores.set([
          `El contacto numero ${i + 1} debe tener la informacion completa( Nombre - Email )`
        ]);
        return false;
      }
    }

    this.errores.set([]);
    return true;
  }

  /**
   * Guarda los contactos y cierra el diálogo
   * Legacy: onClickCerrar() -> buildListDto()
   */
  onGuardar(): void {
    if (!this.validar()) {
      return;
    }

    // Filtrar solo contactos con nombre
    const contactosValidos = this.contactosInterno()
      .filter((c) => c.dsnombre.trim().length > 0)
      .map((c) => ({
        dsnombre: c.dsnombre.trim(),
        dsemail: c.dsemail.trim()
      }));

    this.guardar.emit(contactosValidos);
    this.cerrar();
  }

  /**
   * Cancela y cierra el diálogo
   */
  onCancelar(): void {
    this.cerrar();
  }

  private cerrar(): void {
    this.dialogVisible = false;
    this.visibleChange.emit(false);
  }
}
